import { convertDate } from "@Services/luxonHelper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import _ from "lodash";
import { getUrlFromField, buildConcatenatedValue } from "@Features/forms";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";

// Image and Table Section cases need to be handled later when all grids are moved to SlickGrid

interface FileData {
  name?: string;
  field?: string;
}

interface UrlValue {
  URL?: string;
  displayText?: string;
}

/** A grid row as the default `getValueFromRow` accessor below reads it. */
type CustomFieldRow = { customFields?: Record<string, unknown> } | undefined;

type CustomFieldsRecord = Record<string, unknown>;

const fileFormatter = (val: unknown) => {
  const container = document.createElement("div");
  if (!Array.isArray(val) || val.length === 0) {
    container.textContent = "-";
    return container;
  }

  container.className = "flex pe-3 overflow-auto gap-4 w-full h-full";

  const root = createRoot(container);
  root.render(
    <>
      {(val as FileData[]).map((data) => (
        <span
          key={data?.name || data?.field}
          className="flex gap-3 border-solid border-2 border-button_light w-fit rounded-lg pe-2 items-center"
        >
          <span className="w-14 h-10 flex object-contain">{getFileIcon(data.name)}</span>
          <span>{data.name}</span>
        </span>
      ))}
    </>,
  );

  return container;
};

const dateFormatter = (question: QuestionField, val: number) => {
  const container = document.createElement("div");
  const dateType = question?.displayAs === "dateOnly" ? "NormalDate" : "dateForTooltip";
  const content = String(convertDate(Number(val), dateType));
  // Plain DOM + `data-cube-tip`, matching the STANDARD Date formatter in Formatters.tsx, which
  // already does exactly this. This used to mount a React root per cell rendering a MUI tooltip,
  // and a MUI tooltip has no truncation check - it opens on hover of the cell whether or not the
  // text is clipped. That is the reported behaviour: a tooltip on every custom-field date cell
  // regardless of overflow.
  //
  // The delegated `gridCellTooltip` singleton is already installed on this line (SlickGrid1/index.tsx
  // imports "@CubeComponents/html/gridCellTooltip") and shows a tooltip ONLY when the element really
  // overflows - `scrollWidth > clientWidth + 1 || scrollHeight > clientHeight + 1`. Handing it
  // `data-cube-tip` therefore gives the cell ONE tooltip, on the same side as every other grid cell,
  // and only when there is hidden text to reveal. Its `absorbNativeTitle` only absorbs a native
  // `title` attribute, so it could never have de-duplicated the MUI one.
  container.style.cssText = "width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
  container.textContent = content;
  container.setAttribute("data-cube-tip", content);
  return container;
};

const urlFormatter = (val: unknown) => {
  const container = document.createElement("div");
  container.className = "text-gradient_color cursor-pointer underline underline-offset-2";
  container.onclick = (e: MouseEvent) => {
    e.stopPropagation();
    const url = getUrlFromField(_.get(val as UrlValue, "URL", ""));
    window.open(url);
  };
  const root = createRoot(container);
  root.render(<span>{_.get(val as UrlValue, "displayText", "")}</span>);
  return container;
};

export const getSubColumns = (
  section: FormTemplateField,
  getFieldKey: (questionId: string) => string = (questionId: string) => `customFields.${questionId}`,
  getValueFromRow: (row: CustomFieldRow, questionId: string) => unknown = (row: CustomFieldRow, questionId: string) =>
    row?.customFields?.[questionId],
) => {
  const { questionFields = [], sectionHeading, _id } = section;
  return questionFields?.map((question: QuestionField) => {
    const questionId = question._id;
    const fieldKey = getFieldKey(questionId);
    const columnGroup = sectionHeading;
    const typeMap: Record<string, string> = {
      "single-line": "Text",
      "multi-line": "Text",
      "date-time": "Date",
      "multi-select": "MultiSelect",
    };

    const filterType = typeMap[question.fieldType] || "";

    // Concatenate has no stored value of its own — it is a live formula over other fields. Rebuild
    // it here from the row's sibling values (read through the same accessor) so the grid column
    // shows the current concatenation and reflects later edits, matching how it is displayed
    // elsewhere via buildConcatenatedValue.
    const resolveValue = (row: unknown) => {
      if (question.fieldType === "concatenate") {
        const valueById = new Map<string, string>();
        (question.selectedFields ?? []).forEach((sourceId: string) => {
          const sourceValue = getValueFromRow(row as CustomFieldRow, sourceId);
          valueById.set(sourceId, sourceValue === undefined || sourceValue === null ? "" : String(sourceValue));
        });
        return buildConcatenatedValue(question.selectedFields, valueById, question.seperator);
      }
      return getValueFromRow(row as CustomFieldRow, questionId);
    };

    const column: Record<string, unknown> = {
      id: fieldKey,
      field: fieldKey,
      name: question.questionValue,
      columnGroup,
      minWidth: 150,
      allowFiltering: true,
      allowSorting: true,
      filterType: filterType,
      initiallyHidden: true,
      columnGroupId: _id,
      _valueGetter: (args: CustomFieldsRecord) => {
        const value = resolveValue(args);
        if (value === undefined || value === "" || value === null || (Array.isArray(value) && value.length === 0)) {
          return "-";
        } else if (question.fieldType === "multi-select" && Array.isArray(value)) {
          return (value as string[]).join(", ");
        } else if (question.fieldType === "url") {
          return (value as UrlValue).displayText;
        } else if (question.fieldType === "fileUpload" && Array.isArray(value)) {
          return value;
        } else if (question.fieldType === "image" && Array.isArray(value)) {
          return value;
        }
        return value;
      },
      formatter: (_data: number, _value: number, _a: unknown, _b: unknown, params: unknown) => {
        const val = resolveValue(params);
        if (val === undefined || val === "" || val === null || (Array.isArray(val) && val.length === 0)) {
          return "-";
        } else if (question.fieldType === "multi-select" && Array.isArray(val)) {
          return (val as string[]).join(", ");
        } else if (question.fieldType === "date-time") {
          return dateFormatter(question, val as number);
        } else if (question.fieldType === "url") {
          return urlFormatter(val);
        } else if (question.fieldType === "image") {
          return val;
        } else if (question.fieldType === "fileUpload") {
          return fileFormatter(val);
        }
        return val;
      },
    };

    if (["image", "fileUpload", "look-up", "multi-select"].includes(question.fieldType)) {
      column.allowFiltering = false;
      column.allowSorting = false;
    } else if (["single-line", "multi-line", "single-select", "url", "concatenate"].includes(question.fieldType)) {
      column.filterType = "Text";
    } else if (["number", "autoNumber"].includes(question.fieldType)) {
      column.filterType = "Number";
    } else if (question.fieldType == "date-time") {
      column.filterType = "Date";
    }

    return column;
  });
};
